import React, { useState } from 'react';
import { ExternalLink, Building2, Users, DollarSign, TrendingUp, Briefcase, MapPin } from 'lucide-react';
import '../styles/CompanyHoverCard.css';

const CompanyHoverCard = ({ companyData, isLoading, error }) => {
    const [showFullSummary, setShowFullSummary] = useState(false);

    if (isLoading) {
        return (
            <div className="company-hover-card">
                <div className="company-hover-loading">
                    <div className="loading-spinner-company"></div>
                    <p>Loading company info...</p>
                </div>
            </div>
        );
    }

    if (error || !companyData) {
        // Check if this is a "Knowledge Store" error (status 400)
        const isKnowledgeStoreError = error?.status === 400 ||
            error?.message?.includes('Knowledge Store');

        return (
            <div className="company-hover-card company-hover-error">
                {isKnowledgeStoreError ? (
                    <p className="analyst-message">
                        Thank you for your interest. Our analyst will update the data within 3-4 working days. We appreciate your patience.
                    </p>
                ) : (
                    <p className="error-message">
                        {error?.message || "Company not found."}
                    </p>
                )}
            </div>
        );
    }

    // Helper function to truncate summary to first 3 lines
    const getTruncatedSummary = (text) => {
        if (!text) return '';

        // Check if text has line breaks
        const lines = text.split('\n');
        if (lines.length > 3) {
            return lines.slice(0, 3).join('\n') + '...';
        }

        // If no line breaks, estimate based on character count (~80 chars per line)
        const estimatedLines = Math.ceil(text.length / 80);
        if (estimatedLines > 3) {
            // Truncate to approximately 3 lines worth of characters (240 chars)
            return text.substring(0, 240).trim() + '...';
        }

        return text;
    };

    // Helper function to safely format numbers and avoid NaN display
    const formatNumber = (value, formatter) => {
        if (value == null || value === undefined || isNaN(value) || value === 0) {
            return null;
        }
        return formatter ? formatter(value) : value;
    };

    const {
        longName,
        logo,
        website,
        symbol,
        exchange,
        industry,
        sector,
        fullTimeEmployees,
        marketCap,
        totalRevenue,
        longBusinessSummary,
        city,
        state,
        country
    } = companyData;

    const summaryToShow = showFullSummary
        ? longBusinessSummary
        : getTruncatedSummary(longBusinessSummary);

    const needsExpand = longBusinessSummary && (
        longBusinessSummary.split('\n').length > 3 || longBusinessSummary.length > 240
    );

    return (
        <div className="company-hover-card">
            <div className="company-hover-header">
                {logo && (
                    <div className="company-logo">
                        <img src={logo} alt={`${longName} logo`} onError={(e) => e.target.style.display = 'none'} />
                    </div>
                )}
                <div className="company-header-info">
                    {longName && <h3 className="company-name">{longName}</h3>}
                    {website && (
                        <a
                            href={website.startsWith('http') ? website : `https://${website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="company-website"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <ExternalLink size={12} />
                            {website.replace(/^https?:\/\/(www\.)?/, '')}
                        </a>
                    )}
                </div>
            </div>

            <div className="company-hover-body">
                <div className="company-info-grid">
                    {(symbol || exchange) && (
                        <div className="info-item">
                            <Building2 size={16} className="info-icon" />
                            <div className="info-content">
                                <span className="info-label">Exchange & Stock Ticker</span>
                                <span className="info-value">
                                    {exchange && symbol ? `${exchange} : ${symbol}` : exchange || symbol}
                                </span>
                            </div>
                        </div>
                    )}

                    {industry && (
                        <div className="info-item">
                            <Briefcase size={16} className="info-icon" />
                            <div className="info-content">
                                <span className="info-label">Industry</span>
                                <span className="info-value">{industry}</span>
                            </div>
                        </div>
                    )}

                    {sector && (
                        <div className="info-item">
                            <TrendingUp size={16} className="info-icon" />
                            <div className="info-content">
                                <span className="info-label">Sector</span>
                                <span className="info-value">{sector}</span>
                            </div>
                        </div>
                    )}

                    {formatNumber(fullTimeEmployees, (val) => val.toLocaleString()) && (
                        <div className="info-item">
                            <Users size={16} className="info-icon" />
                            <div className="info-content">
                                <span className="info-label">Employees</span>
                                <span className="info-value">{formatNumber(fullTimeEmployees, (val) => val.toLocaleString())}</span>
                            </div>
                        </div>
                    )}

                    {formatNumber(marketCap, (val) => `$${(val / 1e9).toFixed(1)}B`) && (
                        <div className="info-item">
                            <DollarSign size={16} className="info-icon" />
                            <div className="info-content">
                                <span className="info-label">Market Cap</span>
                                <span className="info-value">{formatNumber(marketCap, (val) => `$${(val / 1e9).toFixed(1)}B`)}</span>
                            </div>
                        </div>
                    )}

                    {formatNumber(totalRevenue, (val) => `$${(val / 1e9).toFixed(1)}B`) && (
                        <div className="info-item">
                            <DollarSign size={16} className="info-icon" />
                            <div className="info-content">
                                <span className="info-label">Revenue</span>
                                <span className="info-value">{formatNumber(totalRevenue, (val) => `$${(val / 1e9).toFixed(1)}B`)}</span>
                            </div>
                        </div>
                    )}

                    {(city || state || country) && (
                        <div className="info-item">
                            <MapPin size={16} className="info-icon" />
                            <div className="info-content">
                                <span className="info-label">Location</span>
                                <span className="info-value">
                                    {[city, state, country].filter(Boolean).join(', ')}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {longBusinessSummary && (
                    <div className="company-summary">
                        <p className={`summary-text ${showFullSummary ? 'expanded' : 'collapsed'}`}>
                            {summaryToShow}
                        </p>
                        {needsExpand && (
                            <button
                                className="show-more-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowFullSummary(!showFullSummary);
                                }}
                            >
                                {showFullSummary ? 'Less' : 'More'}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CompanyHoverCard;
